// Conversion des cartes RiftHub (CardRecord) vers le format du moteur de jeu.
import type { CardRecord } from "@/lib/types";
import type { SavedDeck } from "@/lib/local-store";
import type { Card } from "./types";

/** "[Assault 3] (+3 :rb_might: …)" → "Assault 3 (+3 Might …)" pour les parseurs du moteur. */
export function toEngineText(text: string | null | undefined): string {
  return (text ?? "")
    .replace(/:rb_might:/g, "Might")
    .replace(/:rb_exhaust:/g, "Exhaust")
    .replace(/:rb_rune_rainbow:/g, "any rune")
    .replace(/:rb_rune_([a-z]+):/g, (_, d: string) => `${d[0].toUpperCase()}${d.slice(1)} rune`)
    .replace(/:rb_[a-z_]+:/g, "")
    .replace(/\[([^\]]+)\]/g, "$1")
    .replace(/\)(?=\S)/g, ") ")
    .trim();
}

function engineType(c: CardRecord): string {
  const sup = (c.supertype ?? "").toLowerCase();
  switch (c.type) {
    case "Unit":
      if (sup === "token") return "Token";
      if (sup === "champion") return "Champion Unit";
      if (sup === "signature") return "Signature Unit";
      return "Unit";
    case "Spell":
      return sup === "signature" ? "Signature Spell" : "Spell";
    default:
      return c.type ?? "Unknown";
  }
}

export function toEngineCard(c: CardRecord): Card {
  return {
    id: c.id,
    number: c.collectorNumber != null ? String(c.collectorNumber) : undefined,
    code: c.setId && c.collectorNumber != null ? `${c.setId}-${c.collectorNumber}` : undefined,
    name: c.name,
    cleanName: c.cleanName ?? undefined,
    images: c.imageUrl ? { small: c.imageUrl, large: c.imageUrl } : undefined,
    set: c.setId ? { id: c.setId, name: c.setLabel ?? c.setId } : undefined,
    rarity: c.rarity ?? undefined,
    cardType: engineType(c),
    domain: c.domains.filter((d) => d !== "Colorless").join(";"),
    energyCost: String(c.energy ?? 0),
    powerCost: String(c.power ?? 0),
    might: String(c.might ?? 0),
    description: toEngineText(c.text),
  };
}

const ALT_RARITIES = new Set(["Showcase", "Promo"]);

/**
 * Une seule impression par nom de carte (on préfère l'impression standard
 * aux Showcase / Promo), pour les decks aléatoires et le choix des légendes.
 */
export function uniquePrints(cards: CardRecord[]): CardRecord[] {
  const byName = new Map<string, CardRecord>();
  for (const c of cards) {
    const key = c.name.replace(/\s*\((?:alternate art|overnumbered|signature)[^)]*\)\s*$/iu, "").toLowerCase();
    const prev = byName.get(key);
    if (!prev || (ALT_RARITIES.has(prev.rarity ?? "") && !ALT_RARITIES.has(c.rarity ?? ""))) {
      byName.set(key, c);
    }
  }
  return [...byName.values()];
}

export interface EngineDeck {
  legend: Card;
  /** deck principal (champion choisi en premier) */
  deck: Card[];
  battlefields: Card[];
}

/** Deck RiftHub sauvegardé → deck jouable par le moteur (null si la légende est introuvable). */
export function savedDeckToEngine(saved: SavedDeck, byId: Map<string, CardRecord>): EngineDeck | null {
  const legend = byId.get(saved.legendId);
  if (!legend) return null;
  const deck: Card[] = [];
  for (const e of saved.entries) {
    const c = byId.get(e.cardId);
    if (!c || c.type === "Rune" || c.type === "Legend" || c.type === "Battlefield") continue;
    for (let i = 0; i < e.quantity; i++) deck.push(toEngineCard(c));
  }
  // Le moteur prend le premier champion du deck comme champion choisi
  if (saved.championId) {
    const i = deck.findIndex((c) => c.id === saved.championId);
    if (i > 0) deck.unshift(...deck.splice(i, 1));
  }
  const battlefields = saved.battlefieldIds
    .map((id) => byId.get(id))
    .filter((c): c is CardRecord => !!c)
    .map(toEngineCard);
  return { legend: toEngineCard(legend), deck, battlefields };
}
